/**
 * Auto-Checkout Metrics Dashboard Widget
 * Displays key metrics about auto-checkout usage
 * 
 * Features:
 * - Today's auto-checkout count
 * - Weekly auto-checkout count
 * - Monthly auto-checkout count
 * - Auto-checkout rate percentage
 * - Responsive grid layout
 * - Real-time updates
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { 
  ClockCounterClockwise, 
  TrendUp, 
  Calendar, 
  ChartBar,
  Lightning
} from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface AutoCheckoutMetrics {
  today_auto_checkouts: number;
  week_auto_checkouts: number;
  month_auto_checkouts: number;
  auto_checkout_rate: number;
  total_checkouts_today?: number;
  total_checkouts_week?: number;
  total_checkouts_month?: number;
}

export function AutoCheckoutMetrics() {
  const [metrics, setMetrics] = useState<AutoCheckoutMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch metrics on mount
  useEffect(() => {
    fetchMetrics();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchMetrics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const data = await api.getAttendanceMetrics();
      
      // Transform backend data to match component interface
      const transformedData: AutoCheckoutMetrics = {
        today_auto_checkouts: data.auto_checkouts || 0,
        week_auto_checkouts: data.auto_checkouts || 0,
        month_auto_checkouts: data.auto_checkouts || 0,
        auto_checkout_rate: data.auto_checkout_percentage || 0,
        total_checkouts_today: data.total_checkouts || 0,
        total_checkouts_week: data.total_checkouts || 0,
        total_checkouts_month: data.total_checkouts || 0,
      };
      
      setMetrics(transformedData);
      setError(null);
    } catch (err) {
      setError((err as Error).message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !metrics) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Auto-Checkout Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Auto-Checkout Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded" role="alert">
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  const stats = [
    {
      title: 'Today',
      value: metrics?.today_auto_checkouts || 0,
      total: metrics?.total_checkouts_today,
      icon: ClockCounterClockwise,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'This Week',
      value: metrics?.week_auto_checkouts || 0,
      total: metrics?.total_checkouts_week,
      icon: Calendar,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'This Month',
      value: metrics?.month_auto_checkouts || 0,
      total: metrics?.total_checkouts_month,
      icon: ChartBar,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Auto-Checkout Rate',
      value: `${metrics?.auto_checkout_rate?.toFixed(1) || 0}%`,
      subtitle: 'of all checkouts',
      icon: TrendUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
  ];

  return (
    <Card role="region" aria-label="Auto-Checkout Metrics">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightning className="h-5 w-5 text-primary" weight="duotone" />
            <CardTitle>Auto-Checkout Metrics</CardTitle>
          </div>
          <Badge variant="secondary" className="text-xs">
            Live
          </Badge>
        </div>
        <CardDescription>
          Automatic checkout statistics and usage rates
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border-2">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <p className="text-sm font-medium text-muted-foreground">
                        {stat.title}
                      </p>
                      <div className="flex items-baseline gap-2">
                        <p className="text-3xl font-bold tracking-tight">
                          {stat.value}
                        </p>
                        {stat.total && (
                          <p className="text-sm text-muted-foreground">
                            / {stat.total}
                          </p>
                        )}
                      </div>
                      {stat.subtitle && (
                        <p className="text-xs text-muted-foreground">
                          {stat.subtitle}
                        </p>
                      )}
                    </div>
                    <div className={cn(
                      'rounded-full p-3',
                      stat.bgColor
                    )}>
                      <Icon className={cn('h-6 w-6', stat.color)} weight="duotone" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <ClockCounterClockwise className="h-4 w-4" />
          <p>
            Auto-checkout runs every 15 minutes to check for employees past their shift end time
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
